import React from "react";
import { TrendingUp, TrendingDown, MessageSquare, BookOpen, Target, FileText } from "lucide-react";
import { useSelector } from "react-redux";
import {
  getFeedbackForm,
  getProfileForm,
  getShowFinalRemarks,
} from "../../../../redux/reducer/incbotFeedbackReducer";
import { QUESTION_TYPES } from "../../../../constants";
import PsychometricFinalRemarksView from "./PsychometricFinalRemarksView";

const FinalRemarksView: React.FC = () => {
  const feedbackForm = useSelector(getFeedbackForm);
  const profileForm = useSelector(getProfileForm);
  const showFinalRemarks = useSelector(getShowFinalRemarks);

  if (profileForm?.interviewType === "psychometric_assessment") {
    return <PsychometricFinalRemarksView />;
  }

  const parseRemarks = (data: string) => {
    if (!data) return {};
    const sections: Record<string, string[]> = {};
    let currentSection = "";

    data.split("\n").forEach((line) => {
      const trimmedLine = line.trim();
      if (!trimmedLine) return;

      if (trimmedLine.endsWith(":")) {
        currentSection = trimmedLine.slice(0, -1);
        sections[currentSection] = [];
      } else if (trimmedLine.startsWith("-") && currentSection) {
        sections[currentSection].push(trimmedLine.substring(1).trim());
      }
    });

    return sections;
  };

  const remarksData = parseRemarks(feedbackForm?.finalRemarks?.data || "");

  const strengthsWeaknesses = [
    {
      category: "Strengths",
      icon: <TrendingUp className="w-4 h-4 md:w-5 md:h-5" />,
      color: "text-blue-600",
      items: remarksData["Strengths"] || [],
    },
    {
      category: "Areas of Improvement",
      icon: <TrendingDown className="w-4 h-4 md:w-5 md:h-5" />,
      color: "text-red-600",
      items: remarksData["Areas of Improvement"] || remarksData["Weaknesses"] || [],
    },
  ];

  const assessmentSections = [
    {
      title: "Communication Skills",
      icon: <MessageSquare className="w-4 h-4 md:w-5 md:h-5" />,
      color: "text-purple-600",
      items: remarksData["Communication Skills"] || [],
    },
    {
      title: "Grammatical Correctness",
      icon: <BookOpen className="w-4 h-4 md:w-5 md:h-5" />,
      color: "text-green-600",
      items: remarksData["Grammatical Correctness"] || [],
    },
    {
      title: "Clarity",
      icon: <Target className="w-4 h-4 md:w-5 md:h-5" />,
      color: "text-blue-600",
      items: remarksData["Clarity"] || [],
    },
  ];

  let aptitudeScoreSection: any = null;
  let codingSkillSection: any = null;
  const aptitudeSectionsData = feedbackForm?.aptitudeSectionData?.sectionsData || [];
  const totalAptitudeQuestions = aptitudeSectionsData.reduce(
    (sum: number, section: any) => sum + (section.responses?.length || 0),
    0
  );
  const totalAptitudeScore = aptitudeSectionsData.reduce(
    (sum: number, section: any) => sum + (section.totalMarks || 0),
    0
  );
  const hasCodingQuestions =
    (feedbackForm?.questions?.data || [])?.filter(
      (question: any) => question.questionType === QUESTION_TYPES.CODING
    )?.length > 0;

  if (feedbackForm?.codingAndAptitude && totalAptitudeQuestions > 0) {
    aptitudeScoreSection = {
      title: "Aptitude Score",
      icon: <FileText className="w-4 h-4 md:w-5 md:h-5" />,
      color: "text-teal-600",
      items: [
        `The candidate answered ${totalAptitudeScore} question${totalAptitudeScore > 1 ? "s" : ""} correctly out of ${totalAptitudeQuestions}.`,
      ],
    };
  }

  if (feedbackForm?.codingAndAptitude && hasCodingQuestions) {
    codingSkillSection = {
      title: "Coding Skills",
      icon: <FileText className="w-4 h-4 md:w-5 md:h-5" />,
      color: "text-orange-600",
      items: feedbackForm?.codingFinalRemark || [],
    };
  }

  let filteredSections;

  if (feedbackForm?.codingAndAptitude) {
    filteredSections = [
      ...(codingSkillSection && codingSkillSection?.items?.length > 0 ? [codingSkillSection] : []),
      ...(aptitudeScoreSection ? [aptitudeScoreSection] : []),
    ];
  } else {
    filteredSections = [
      ...strengthsWeaknesses.filter((section) => {
        const permitted =
          showFinalRemarks[section.category] ||
          (section.category === "Areas of Improvement" && showFinalRemarks["Weaknesses"]);
        return permitted && section.items.length > 0;
      }),
      ...assessmentSections.filter(
        (section) => showFinalRemarks[section.title] && section.items.length > 0
      ),
    ];
  }

  return (
    <div className="p-3 md:p-6">
      <div className="md:hidden print:flex w-100 p-1 print:p-2 rounded-md border-b-4 border-teal-500 bg-teal-50 text-teal-500 text-md print:text-xl flex justify-center font-semibold mb-3 md:mb-6">
        Final Remarks
      </div>

      {feedbackForm?.AIGenTextBanner && (
        <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 p-4 md:p-6 rounded-md mb-3">
          <p className="text-sm md:text-md font-semibold text-red-500 mb-1">
            AI-Assisted Response Detected
          </p>
          <p className="text-xs md:text-sm leading-relaxed">
            Our system has identified that the candidate may have relied on AI-generated responses
            during the interview. While the answers provided appear correct, they may not accurately
            reflect the candidate’s own knowledge or problem-solving abilities. Please consider this
            factor when making your hiring decision.
          </p>

          {feedbackForm?.AIGenText && (
            <div className="italic text-[8px] md:text-xs mt-4 max-md:text-center">
              Note: Responses under 150 words may not be accurately flagged as AI-assisted, as
              shorter answers can affect detection reliability.
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 md:gap-8">
        {filteredSections.map((section: any, index: any) => (
          <div key={index} className="bg-white p-4 md:p-6 rounded-lg border border-gray-200">
            <div className="flex items-center space-x-2 mb-4">
              <div className={section.color}>{section.icon}</div>
              <h2 className="text-md md:text-lg font-semibold text-gray-900">
                {section.category || section.title}
              </h2>
            </div>
            <div className="space-y-2 md:space-y-4">
              {section.items.map((item: any, itemIndex: any) => (
                <div key={itemIndex} className="flex items-start space-x-2">
                  <div className="w-1 h-1 md:w-2 md:h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-xs md:text-sm text-gray-700">{item}</p>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default FinalRemarksView;
